import React, { useMemo, useState } from 'react';
import { FaEye, FaFileExcel, FaLayerGroup, FaListUl, FaSpinner, FaTable, FaThList } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import { Pagination, PaginationType } from '../../utils/pagination.utils';
import { ExcelDownload } from '../../utils/excel.utils';
import { SelectWithSearch, type SelectOption } from '../../utils/select-with-search';
import SiteRouteModal, { type SiteRouteModalTarget } from './components/SiteRouteModal';

/**
 * FIX — the report was not showing the full list:
 * fetchRows/fetchExportData used to re-filter the returned PAGE client-side by
 * date and then set `total = clientFilteredData.length`, which collapsed
 * total_pages to 1 and made every row beyond page 1 unreachable whenever a
 * date filter was applied. That client filter was a band-aid for the backend's
 * broken (UTC vs IST) work_date filtering — now fixed server-side — so both
 * the re-filter and the total override are removed and the page trusts the
 * backend's pagination.
 */

interface DailyTravelRow {
  [key: string]: any;
}

interface DailyTravelFilters {
  report_date_from: string;
  report_date_to: string;
  user_id: string;
  role: string;
}

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const DEFAULT_FILTERS: DailyTravelFilters = {
  report_date_from: '',
  report_date_to: '',
  user_id: '',
  role: '',
};

/** One hop between two consecutive sites of the day. */
interface SiteLeg {
  sequence: number;
  from: string;
  to: string;
  distance_km: number | null;
}

/**
 * The string cells of a row. Every key here is safe to render directly, which
 * is what lets the table, the cards and the Excel export share TABLE_COLUMNS.
 */
interface NormalizedDailyTravelRow {
  workDate: string;
  userName: string;
  employeeCode: string;
  reportingState: string;
  fieldWorkState: string;
  fieldWorksStartedCount: string;
  homeDistance: string;
  attendanceDistance: string;
  minimumDistance: string;
  siteToSiteDistances: string;
  checkinLatLong: string;
  reportingLatLong: string;
  departmentGroup: string;
  reportingPerson: string;
}

/** A row plus the structured data the Site-to-Site modal needs. */
interface DailyTravelTableRow extends NormalizedDailyTravelRow {
  siteToSiteLegs: SiteLeg[];
  /** Identifies the engineer-day the route viewer asks the API for. */
  userId: number | null;
  /** The raw IST work date (YYYY-MM-DD), not the display string. */
  workDateRaw: string | null;
}

interface CellContext {
  onViewSiteLegs: (row: DailyTravelTableRow) => void;
}

const TABLE_COLUMNS: {
  key: keyof NormalizedDailyTravelRow;
  label: string;
  /** Overrides the default nowrap cell, for columns holding long text. */
  cellClassName?: string;
  /** Renders the cell itself; falls back to the plain string at `key`. */
  render?: (row: DailyTravelTableRow, ctx: CellContext) => React.ReactNode;
}[] = [
  { key: 'workDate',               label: 'Work Date' },
  { key: 'userName',               label: 'User Name' },
  { key: 'employeeCode',           label: 'User Emp Id' },
  { key: 'reportingState',         label: 'Reporting State' },
  { key: 'fieldWorkState',         label: 'Field Work State' },
  { key: 'fieldWorksStartedCount', label: 'Field Works Started Count' },
  { key: 'homeDistance',           label: 'Home Distance Travel' },
  { key: 'attendanceDistance',     label: 'Attendance Distance Travel' },
  { key: 'minimumDistance',        label: 'Minimum Distance Travel' },
  {
    key: 'siteToSiteDistances',
    label: 'Site to Site Distance',
    // A day's route holds far too much for one cell — a map, both ways of
    // measuring it and eight columns per stop — so the table shows a button and
    // the detail lives in the route viewer. The Excel export still writes the
    // joined string, since a workbook has nowhere to put a button.
    //
    // The button is offered on every day that identifies an engineer and a date,
    // not only on days that already have hops between sites: the viewer computes
    // the route itself, and a single-site day still has a journey to show.
    render: (row, { onViewSiteLegs }) =>
      row.userId && row.workDateRaw ? (
        <button
          type="button"
          onClick={() => onViewSiteLegs(row)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-800 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
        >
          <FaEye className="w-3 h-3" />
          View route{row.siteToSiteLegs.length > 0 ? ` (${row.siteToSiteLegs.length})` : ''}
        </button>
      ) : (
        <span className="text-gray-500">{row.siteToSiteDistances}</span>
      ),
  },
  { key: 'checkinLatLong',         label: 'Checkin LatLong' },
  { key: 'reportingLatLong',       label: 'Reporting Lat Long' },
  { key: 'departmentGroup',        label: 'Department Group' },
  { key: 'reportingPerson',        label: 'Reporting Person' },
];

/** Card body fields — user name and work date already head each card. */
const CARD_COLUMNS = TABLE_COLUMNS.filter(({ key }) => key !== 'userName' && key !== 'workDate');

const withFallback = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'NA';
  return String(value);
};

/**
 * A day whose distances the backend has not finished precomputing reports
 * `distance_status: 'pending'`. Its blank cells mean "not yet", not "none",
 * so they must not read as NA.
 */
const isPendingDistance = (row: DailyTravelRow) =>
  String(row.distance_status || '').toLowerCase() === 'pending';

const formatDistanceValue = (value: unknown, pending = false) => {
  if (value === null || value === undefined || value === '') {
    return pending ? 'Calculating...' : 'NA';
  }
  const asString = String(value).trim();
  if (!asString) return 'NA';
  if (/[a-zA-Z]/.test(asString)) return asString.replace(/\s+/g, ' ');
  const num = Number(asString);
  if (!Number.isFinite(num)) return asString;
  return `${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)} km`;
};

/**
 * Work Date is a calendar day, not a moment: the report groups a whole day of
 * travel under it. The shared formatDate appends a time, which made every row
 * read "Sep 5, 2026, 12:00 AM" — a time that means nothing here — so this
 * formats the day alone.
 */
const formatDateCell = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'NA';
  const asString = String(value);
  const match = asString.match(/^(d{4})-(d{2})-(d{2})/);
  if (match) {
    // Already an IST calendar day — read it as one rather than letting the
    // Date constructor shift it into the browser timezone.
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  const parsed = new Date(asString);
  if (Number.isNaN(parsed.getTime())) return asString;
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const pickFirst = (row: DailyTravelRow, keys: string[]) => {
  for (const key of keys) {
    const value = row[key];
    if (value !== null && value !== undefined && value !== '') {
      return value;
    }
  }
  return '';
};

/** Builds "lat, long" from a pre-joined string or a pair of coordinate keys. */
const formatLatLong = (row: DailyTravelRow, joinedKeys: string[], latKeys: string[], lngKeys: string[]) => {
  const joined = pickFirst(row, joinedKeys);
  if (joined) return String(joined);
  const lat = pickFirst(row, latKeys);
  const lng = pickFirst(row, lngKeys);
  if (lat === '' || lng === '') return 'NA';
  return `${lat}, ${lng}`;
};

/** YYYY-MM-DD for the route viewer, from whichever date key the API sent. */
const toWorkDateRaw = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const asString = String(value);
  const match = asString.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = new Date(asString);
  if (Number.isNaN(parsed.getTime())) return null;
  // The API's work dates are IST calendar days; shift before slicing so a
  // late-evening timestamp does not land on the previous date.
  return new Date(parsed.getTime() + 330 * 60 * 1000).toISOString().slice(0, 10);
};

const normalizeDailyTravelRow = (row: DailyTravelRow): DailyTravelTableRow => {
  const checkIn = row.check_in_attendance || {};
  const startedCount = pickFirst(row, ['field_works_started_count', 'field_work_started_count']);
  const pending = isPendingDistance(row);
  const rawUserId = Number(pickFirst(row, ['user_id', 'employee_id_ref', 'engineer_id']));

  return {
    userId: Number.isFinite(rawUserId) && rawUserId > 0 ? rawUserId : null,
    workDateRaw: toWorkDateRaw(pickFirst(row, ['work_date', 'date', 'report_date', 'travel_date'])),
    workDate: formatDateCell(pickFirst(row, ['work_date', 'date', 'report_date', 'travel_date'])),
    userName: withFallback(pickFirst(row, ['user_name', 'employee_name', 'name', 'employee'])),
    employeeCode: withFallback(pickFirst(row, ['employee_code', 'user_emp_id', 'employee_id'])),
    reportingState: withFallback(pickFirst(row, ['reporting_state'])),
    fieldWorkState: withFallback(pickFirst(row, ['field_work_state'])),
    // A day with visits but no count from the API still reports the sites it lists.
    fieldWorksStartedCount: withFallback(
      startedCount !== '' ? startedCount : (row.site_visit_sequence?.length ?? '')
    ),
    homeDistance: formatDistanceValue(
      pickFirst(row, ['home_distance_travel_km', 'home_distance_travel']),
      pending
    ),
    attendanceDistance: formatDistanceValue(
      pickFirst(row, ['attendance_distance_travel_km', 'attendance_distance_travel']),
      pending
    ),
    minimumDistance: formatDistanceValue(
      pickFirst(row, ['minimum_distance_travel_km', 'minimum_distance_travel']),
      pending
    ),
    // Pre-joined by the API as "SIT001 -> SIT002 = 10.5 km, ...". A day with a
    // single site has no hop between sites, so it stays NA rather than 0.
    siteToSiteDistances:
      pending && !row.site_to_site_distances
        ? 'Calculating...'
        : withFallback(pickFirst(row, ['site_to_site_distances'])),
    siteToSiteLegs: Array.isArray(row.site_to_site_segments)
      ? (row.site_to_site_segments as SiteLeg[])
      : [],
    checkinLatLong: formatLatLong(
      { ...row, check_in_latitude: checkIn.latitude, check_in_longitude: checkIn.longitude },
      ['checkin_lat_long'],
      ['checkin_latitude', 'check_in_latitude'],
      ['checkin_longitude', 'check_in_longitude']
    ),
    reportingLatLong: formatLatLong(row, ['reporting_lat_long'], ['reporting_latitude'], ['reporting_longitude']),
    departmentGroup: withFallback(pickFirst(row, ['department_group', 'department'])),
    reportingPerson: withFallback(pickFirst(row, ['reporting_person'])),
  };
};

/* ── Detailed view ─────────────────────────────────────────────────────────
 *
 * Summary is one row per engineer-day. Detailed is one row per SITE VISIT
 * within the day, which is why the day's Visit Start / Visit End details
 * repeat down the rows belonging to a day.
 *
 * It is always measured along the attendance route — the columns are named for
 * check-in and check-out, and the "from home" route has neither — and it is
 * scoped to ONE engineer, because a journey belongs to one person.
 */

interface DetailedTravelRow {
  [key: string]: any;
}

const kmCell = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'NA';
  const num = Number(value);
  return Number.isFinite(num) ? `${num.toFixed(2)} km` : String(value);
};

const timeCell = (value: unknown) => {
  if (!value) return 'NA';
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const dateCell = (value: unknown) => {
  if (!value) return 'NA';
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Column order, labels and formatting for the detailed table. The backend
 * sends the same order; this is the one place that says how each value reads,
 * so the table and the Excel export cannot disagree.
 */
const DETAILED_COLUMNS: {
  key: string;
  label: string;
  format?: (value: unknown, row: DetailedTravelRow) => string;
  wrap?: boolean;
}[] = [
  { key: 'work_date',                 label: 'Work Date', format: dateCell },
  { key: 'visit_start_time',          label: 'Visit Start Time', format: timeCell },
  { key: 'visit_start_lat_long',      label: 'Visit Start Lat Long' },
  { key: 'visit_start_location_name', label: 'Visit Start Location Name', wrap: true },
  { key: 'visit_site_id',             label: 'Visit Site ID' },
  { key: 'site_name',                 label: 'Site Name', wrap: true },
  { key: 'check_in_time',             label: 'Check In Time', format: timeCell },
  { key: 'distance_from_previous_km', label: 'Distance from Previous', format: kmCell },
  { key: 'lat_long',                  label: 'Lat Long' },
  { key: 'location_name',             label: 'Location Name', wrap: true },
  { key: 'fw_start',                  label: 'FW Start', format: timeCell },
  { key: 'survey_date',               label: 'Survey Date', format: dateCell },
  // The end of the day is the check-out when there is one; when the engineer
  // never marked one it repeats the check-in, and the row says so rather than
  // quietly showing the start time twice.
  {
    key: 'visit_end_time',
    label: 'Visit End Time',
    format: (value, row) =>
      `${timeCell(value)}${row.visit_end_is_check_out ? '' : ' (no check-out)'}`,
  },
  { key: 'visit_end_lat_long',        label: 'Visit End Lat Long' },
  { key: 'visit_end_location_name',   label: 'Visit End Location Name', wrap: true },
  { key: 'route_path',                label: 'Route Path', wrap: true },
];

const detailedCellText = (row: DetailedTravelRow, column: (typeof DETAILED_COLUMNS)[number]) => {
  const value = row[column.key];
  if (column.format) return column.format(value, row);
  return withFallback(value);
};

const buildDetailedExportRows = (rows: DetailedTravelRow[]) =>
  rows.map((row) =>
    Object.fromEntries(DETAILED_COLUMNS.map((column) => [column.label, detailedCellText(row, column)])),
  );

/** Prefers the API's own message (e.g. the 32-day range limit) over a generic fallback. */
const apiErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

const MAX_REPORT_RANGE_DAYS = 32;

const dateRangeExceedsLimit = (from: string, to: string) => {
  if (!from || !to) return false;
  const fromTime = Date.parse(`${from}T00:00:00Z`);
  const toTime = Date.parse(`${to}T00:00:00Z`);
  return Number.isFinite(fromTime) && Number.isFinite(toTime) &&
    Math.floor((toTime - fromTime) / 86400000) + 1 > MAX_REPORT_RANGE_DAYS;
};

// The workbook takes the joined string, not the button.
const buildExportRows = (rows: DailyTravelTableRow[]) =>
  rows.map((row) =>
    Object.fromEntries(TABLE_COLUMNS.map(({ key, label }) => [label, row[key]]))
  );

const DailyTravelReport: React.FC = () => {
  const [rows, setRows] = useState<DailyTravelRow[]>([]);
  const normalizedRows = useMemo(() => rows.map(normalizeDailyTravelRow), [rows]);
  const [pagination, setPagination] = useState<NormalizedPagination>({
    total_items: 0,
    current_page: 1,
    total_pages: 1,
    items_per_page: 50,
    has_next_page: false,
    has_previous_page: false,
  });
  const [filters, setFilters] = useState<DailyTravelFilters>(DEFAULT_FILTERS);
  const [selectedUser, setSelectedUser] = useState<SelectOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [exportData, setExportData] = useState<Record<string, string | number>[]>([]);
  // 'horizontal' = wide table; 'vertical' = one stacked card per record (no side scrolling).
  const [viewMode, setViewMode] = useState<'horizontal' | 'vertical'>('horizontal');
  const [routeTarget, setRouteTarget] = useState<SiteRouteModalTarget | null>(null);
  // 'summary' = one row per engineer-day (the original report);
  // 'detailed' = one row per site visit, for a single engineer.
  const [reportMode, setReportMode] = useState<'summary' | 'detailed'>('summary');
  const [detailedUser, setDetailedUser] = useState<SelectOption | null>(null);
  const [detailedRows, setDetailedRows] = useState<DetailedTravelRow[]>([]);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [detailedFetched, setDetailedFetched] = useState(false);
  const cellContext: CellContext = {
    onViewSiteLegs: (row) => {
      if (!row.userId || !row.workDateRaw) return;
      setRouteTarget({
        userId: row.userId,
        workDate: row.workDateRaw,
        userName: row.userName,
        workDateLabel: row.workDate,
      });
    },
  };

  const buildParams = (page: number, limit: number) => {
    const params: Record<string, string | number> = { page, limit };
    if (filters.report_date_from) params.report_date_from = filters.report_date_from;
    if (filters.report_date_to) params.report_date_to = filters.report_date_to;
    // compatibility keys in case backend expects different names
    if (filters.report_date_from) params.date_from = filters.report_date_from;
    if (filters.report_date_to) params.date_to = filters.report_date_to;
    if (filters.user_id) params.user_id = Number(filters.user_id);
    if (filters.role) params.role = filters.role;
    return params;
  };

  const fetchRows = async (targetPage = 1, targetLimit = pagination.items_per_page) => {
    if (dateRangeExceedsLimit(filters.report_date_from, filters.report_date_to)) {
      toast.error(`Date From and Date To cannot span more than ${MAX_REPORT_RANGE_DAYS} days.`);
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.get('/reports/daily-travel', {
        params: buildParams(targetPage, targetLimit),
      });

      if (response.data.success) {
        const payload = response.data.data || {};
        const data: DailyTravelRow[] = payload.data || [];
        // FIX: trust the backend page + totals. The old client-side date
        // re-filter set total = filtered page length, which collapsed
        // total_pages to 1 and hid every row beyond the first page.
        const p = payload.pagination || {};
        const total = Number(p.total ?? p.total_items ?? p.total_count) || data.length;
        const currentPage = Number(p.current_page ?? p.page) || targetPage;
        const perPage = Number(p.per_page ?? p.items_per_page ?? p.limit) || targetLimit;
        const totalPages = Number(p.total_pages) || Math.max(1, Math.ceil(total / perPage));

        setRows(data);
        setPagination({
          total_items: total,
          current_page: currentPage,
          total_pages: totalPages,
          items_per_page: perPage,
          has_next_page: Boolean(p.has_next_page ?? currentPage < totalPages),
          has_previous_page: Boolean(p.has_prev_page ?? currentPage > 1),
        });
        setHasFetched(true);
      } else {
        // A 2xx carrying success:false used to fail silently — surface its message too.
        toast.error(response.data?.message || 'Failed to fetch daily travel report');
      }
    } catch (error) {
      console.error(error);
      toast.error(apiErrorMessage(error, 'Failed to fetch daily travel report'));
    } finally {
      setLoading(false);
    }
  };

  const fetchExportData = async () => {
    if (dateRangeExceedsLimit(filters.report_date_from, filters.report_date_to)) {
      toast.error(`Date From and Date To cannot span more than ${MAX_REPORT_RANGE_DAYS} days.`);
      return;
    }
    try {
      setExporting(true);
      const pageSize = 100;
      let currentPage = 1;
      let totalPages = 1;
      const allRows: DailyTravelRow[] = [];

      do {
        const response = await axiosInstance.get('/reports/daily-travel', {
          params: buildParams(currentPage, pageSize),
        });
        if (!response.data.success) {
          toast.error(response.data?.message || 'Failed to prepare daily travel export');
          return;
        }
        const payload = response.data.data || {};
        const data: DailyTravelRow[] = payload.data || [];
        const p = payload.pagination || {};
        // FIX: no client-side re-filtering — the backend date filter is now
        // authoritative, so the export collects every page as-is.
        allRows.push(...data);
        totalPages =
          Number(p.total_pages) ||
          Math.max(1, Math.ceil((Number(p.total ?? p.total_items ?? p.total_count) || allRows.length) / pageSize));
        currentPage += 1;
      } while (currentPage <= totalPages);

      const normalized = allRows.map(normalizeDailyTravelRow);
      setExportData(buildExportRows(normalized));
      setShowExcelModal(true);
    } catch (error) {
      console.error(error);
      toast.error(apiErrorMessage(error, 'Failed to prepare daily travel export'));
    } finally {
      setExporting(false);
    }
  };

  /**
   * The detailed report is a different endpoint, not a different rendering of
   * the summary rows: it expands each day into its visits, which the summary
   * payload does not carry. The engineer is mandatory — a journey belongs to
   * one person — so this refuses to fire without one.
   */
  const fetchDetailedRows = async () => {
    if (!detailedUser) {
      toast.error('Select a user to load the detailed report.');
      return;
    }
    if (!filters.report_date_from || !filters.report_date_to) {
      toast.error('Date From and Date To are required for the detailed report.');
      return;
    }
    if (dateRangeExceedsLimit(filters.report_date_from, filters.report_date_to)) {
      toast.error(`Date From and Date To cannot span more than ${MAX_REPORT_RANGE_DAYS} days.`);
      return;
    }
    try {
      setDetailedLoading(true);
      const response = await axiosInstance.get('/reports/daily-travel/detailed', {
        params: {
          user_id: Number(detailedUser.value),
          report_date_from: filters.report_date_from,
          report_date_to: filters.report_date_to,
        },
      });
      if (response.data.success) {
        setDetailedRows(response.data.data?.data || []);
        setDetailedFetched(true);
      } else {
        toast.error(response.data?.message || 'Failed to fetch the detailed travel report');
      }
    } catch (error) {
      console.error(error);
      toast.error(apiErrorMessage(error, 'Failed to fetch the detailed travel report'));
    } finally {
      setDetailedLoading(false);
    }
  };

  // Already in hand — the detailed endpoint is not paginated, so the export is
  // the rows on screen rather than another sweep of the API.
  const exportDetailedRows = () => {
    if (detailedRows.length === 0) {
      toast.error('Nothing to export — load the detailed report first.');
      return;
    }
    setExportData(buildDetailedExportRows(detailedRows));
    setShowExcelModal(true);
  };

  const detailedStatusMessage = detailedLoading
    ? 'Loading...'
    : !detailedUser
      ? 'Select a user and a date range, then apply filters.'
      : !detailedFetched
        ? 'Apply filters to load the detailed report.'
        : detailedRows.length === 0
          ? 'No visits found for this user in the selected range.'
          : null;

  // Placeholder text shared by both views; null means there are rows to render.
  const statusMessage = loading
    ? 'Loading...'
    : !hasFetched
      ? 'Apply filters to load report.'
      : rows.length === 0
        ? 'No travel records found.'
        : null;

  const paginationAdapter: PaginationType = useMemo(() => ({
    offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
    limit: pagination.items_per_page,
    page: pagination.current_page,
    total_pages: pagination.total_pages,
    total_count: pagination.total_items,
    has_next_page: pagination.has_next_page,
    has_prev_page: pagination.has_previous_page,
    next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
    prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null,
  }), [pagination]);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Daily Travel Report</h1>
            <p className="text-sm text-gray-600 mt-1">
              {reportMode === 'summary'
                ? "Daily field work and travel distance per user. Distances are the day's total across the sites worked."
                : 'Every site visit of one user, in the order they were opened, with the distance from the previous stop.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Report toggle: totals per day vs. the visits behind them */}
            <div className="flex rounded border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setReportMode('summary')}
                aria-pressed={reportMode === 'summary'}
                title="Summary — one row per user per day"
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${reportMode === 'summary' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <FaLayerGroup className="w-4 h-4" /> Summary
              </button>
              <button
                type="button"
                onClick={() => setReportMode('detailed')}
                aria-pressed={reportMode === 'detailed'}
                title="Detailed — one row per site visit, for a single user"
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-l border-gray-200 ${reportMode === 'detailed' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <FaListUl className="w-4 h-4" /> Detailed
              </button>
            </div>

            {/* View toggle: wide table vs. stacked per-record cards.
                Only the summary has a card view — a detailed row is a leg of a
                journey and reads as a row. */}
            {reportMode === 'summary' && (
            <div className="flex rounded border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode('horizontal')}
                aria-pressed={viewMode === 'horizontal'}
                title="Horizontal view — one row per record"
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${viewMode === 'horizontal' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <FaTable className="w-4 h-4" /> Horizontal
              </button>
              <button
                type="button"
                onClick={() => setViewMode('vertical')}
                aria-pressed={viewMode === 'vertical'}
                title="Vertical view — one card per record"
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-l border-gray-200 ${viewMode === 'vertical' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <FaThList className="w-4 h-4" /> Vertical
              </button>
            </div>
            )}
            <button
              onClick={() =>
                reportMode === 'detailed'
                  ? fetchDetailedRows()
                  : fetchRows(1, pagination.items_per_page)
              }
              disabled={reportMode === 'detailed' ? detailedLoading : loading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {(reportMode === 'detailed' ? detailedLoading : loading) ? <FaSpinner className="w-4 h-4 animate-spin" /> : null}
              {(reportMode === 'detailed' ? detailedLoading : loading) ? 'Loading...' : 'Apply Filters'}
            </button>
            <button
              onClick={reportMode === 'detailed' ? exportDetailedRows : fetchExportData}
              disabled={
                reportMode === 'detailed'
                  ? detailedRows.length === 0
                  : !hasFetched || exporting
              }
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
            >
              {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
              {exporting ? 'Preparing...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Mandatory in the detailed view — the report is one person's day,
              and the list is limited to users who share a project with me. */}
          {reportMode === 'detailed' && (
            <div className="block md:col-span-2">
              <span className="block text-xs font-medium text-gray-700 mb-1">
                User <span className="text-red-600">*</span>
              </span>
              <SelectWithSearch
                placeholder="Search user in my projects"
                value={detailedUser}
                onChange={(option) => {
                  setDetailedUser(option);
                  setDetailedRows([]);
                  setDetailedFetched(false);
                }}
                apiEndpoint="/users/my-project-users"
                dataKey="users"
                labelKey="first_name"
                valueKey="id"
                searchParam="search"
                displayFormat={(item: any) => {
                  const name = [item.first_name, item.last_name].filter(Boolean).join(' ').trim();
                  return `${name || `User #${item.id}`}${item.employee_code ? ` (${item.employee_code})` : ''}`;
                }}
                className="w-full"
              />
            </div>
          )}
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Date From</span>
            <input
              type="date"
              value={filters.report_date_from}
              onChange={(e) => setFilters((prev) => ({ ...prev, report_date_from: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Date To</span>
            <input
              type="date"
              value={filters.report_date_to}
              min={filters.report_date_from || undefined}
              max={filters.report_date_from
                ? new Date(new Date(`${filters.report_date_from}T00:00:00Z`).getTime() + (MAX_REPORT_RANGE_DAYS - 1) * 86400000).toISOString().slice(0, 10)
                : undefined}
              onChange={(e) => setFilters((prev) => ({ ...prev, report_date_to: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </label>

          {reportMode === 'summary' && (
          <div className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">User</span>
            <SelectWithSearch
              placeholder="Search user"
              value={selectedUser}
              onChange={(option) => {
                setSelectedUser(option);
                setFilters((prev) => ({ ...prev, user_id: option ? String(option.value) : '' }));
              }}
              apiEndpoint="/users/list"
              dataKey="users"
              labelKey="first_name"
              valueKey="id"
              searchParam="search"
              displayFormat={(item: any) => {
                const name = [item.first_name, item.last_name].filter(Boolean).join(' ').trim();
                return `${name || `User #${item.id}`}${item.employee_code ? ` (${item.employee_code})` : ''}`;
              }}
              className="w-full"
            />
          </div>
          )}

          {reportMode === 'summary' && (
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Role</span>
            <input
              type="text"
              placeholder="Enter role"
              value={filters.role}
              onChange={(e) => setFilters((prev) => ({ ...prev, role: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </label>
          )}
        </div>
      </div>

      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {/* Detailed view: one row per site visit. Not paginated — the range is
            capped server-side, so a user's visits over it fit in one payload. */}
        {reportMode === 'detailed' ? (
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {DETAILED_COLUMNS.map(({ key, label }) => (
                    <th
                      key={key}
                      className="sticky top-0 z-10 bg-gray-50 shadow-[inset_0_-1px_0_0_#e5e7eb] px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {detailedStatusMessage ? (
                  <tr>
                    <td colSpan={DETAILED_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">
                      {detailedStatusMessage}
                    </td>
                  </tr>
                ) : (
                  detailedRows.map((row, index) => (
                    <tr key={`${row.work_date}-${row.site_row_id}-${index}`} className="hover:bg-gray-50">
                      {DETAILED_COLUMNS.map((column) => (
                        <td
                          key={column.key}
                          className={
                            column.wrap
                              ? 'px-4 py-3 text-sm text-gray-700 break-words max-w-xs'
                              : 'px-4 py-3 text-sm text-gray-700 whitespace-nowrap'
                          }
                        >
                          {detailedCellText(row, column)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : /* Vertical view: one card per record, so a wide row reads top-to-bottom
            instead of behind a horizontal scrollbar. */
        viewMode === 'vertical' ? (
          <div className="overflow-auto max-h-[70vh] p-4 space-y-3">
            {statusMessage ? (
              <div className="py-10 text-center text-sm text-gray-600">{statusMessage}</div>
            ) : (
              normalizedRows.map((row, index) => (
                <div key={index} className="border border-gray-200 rounded overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 flex items-baseline justify-between gap-3 border-b border-gray-200">
                    <span className="text-sm font-semibold text-gray-900">{row.userName}</span>
                    <span className="text-xs text-gray-600">{row.workDate}</span>
                  </div>
                  <dl className="divide-y divide-gray-100">
                    {CARD_COLUMNS.map(({ key, label, render }) => (
                      <div key={key} className="flex gap-3 px-4 py-2">
                        <dt className="w-1/2 sm:w-2/5 shrink-0 text-xs font-medium text-gray-600">{label}</dt>
                        <dd className="flex-1 text-sm text-gray-800 break-words">
                          {render ? render(row, cellContext) : row[key]}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))
            )}
          </div>
        ) : (
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full">
            {/* Sticky header: stays visible while the body scrolls vertically.
                Background + shadow live on the th (a thead border scrolls away with sticky cells). */}
            <thead className="bg-gray-50">
              <tr>
                {TABLE_COLUMNS.map(({ key, label }) => (
                  <th
                    key={key}
                    className="sticky top-0 z-10 bg-gray-50 shadow-[inset_0_-1px_0_0_#e5e7eb] px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {statusMessage ? (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">{statusMessage}</td>
                </tr>
              ) : (
                normalizedRows.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {TABLE_COLUMNS.map(({ key, cellClassName, render }) => (
                      <td
                        key={key}
                        className={cellClassName || 'px-4 py-3 text-sm text-gray-700 whitespace-nowrap'}
                      >
                        {render ? render(row, cellContext) : row[key]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}
        {reportMode === 'summary' && hasFetched && (
          <Pagination
            pagination={paginationAdapter}
            onPageChange={(nextPage) => fetchRows(nextPage, pagination.items_per_page)}
            onLimitChange={(nextLimit) => fetchRows(1, nextLimit)}
          />
        )}
      </div>

      <SiteRouteModal target={routeTarget} onClose={() => setRouteTarget(null)} />

      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename={reportMode === 'detailed' ? 'daily_travel_detailed_report' : 'daily_travel_report'}
        title={reportMode === 'detailed' ? 'Export Detailed Daily Travel Report' : 'Export Daily Travel Report'}
        description="Download daily travel report as Excel or CSV"
      />
    </div>
  );
};

export default DailyTravelReport;
